import Button from '@atlaskit/button';
import traceUFOPress from '@atlassian/react-ufo/trace-press';

import React, { useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { redirect } from 'redux-first-router';

import { Dimensions } from 'components/visuals';
import { ORDER_INVOICE_ACTION_PDF_UFO_NAME } from 'util/ufoNameConstants';

import {
  StyledHistoryTableWrapper,
  StyledHistoryWrapper,
  StyledMigratedHistoryTableData,
  StyledMigratedHistoryWrapper,
  StyledParagraph,
  StyledTextLabel,
} from '../licensesExpandedRow/StyledComponents';
import EndUserLicenseKeyDialog from './EndUserLicenseKeyDialog';
import { fetchLicenseById } from '../../duck/actions/license';
import { fetchInvoicePDF } from '../../duck/actions/invoices';
import { removeTimezone } from '../../common/utils';
import { route } from '../../duck/actions/orders';
import { TableRow } from '../styledComponents';
import { messages } from 'modules/mac/messages';

const AccountHistoryItem = ({ period, handleInvoiceDownload, userPartnerId }) => {
  const dispatch = useDispatch();
  const [isModalOpened, setIsModalOpened] = useState(false);
  const { paidDate, orderId, description, partnerId, licenseId } = period;
  const isPurchasedByLoggedInUser = userPartnerId === partnerId?.toString();

  useEffect(() => {
    if (licenseId) {
      dispatch(fetchLicenseById(licenseId));
    }
  }, []);

  return (
    <>
      <StyledTextLabel width="auto" textalign="center">
        <FormattedDate value={removeTimezone(paidDate)} day="2-digit" month="long" year="numeric" />
      </StyledTextLabel>
      <p>
        <Button
          appearance="link"
          key={orderId}
          onClick={(e) => handleInvoiceDownload(e, orderId, isPurchasedByLoggedInUser)}
        >
          AT-{orderId}
        </Button>
        <StyledParagraph padding={`0 ${Dimensions.units(1.25)}px`}>
          {!isPurchasedByLoggedInUser ? (
            <FormattedMessage
              defaultMessage="(Not purchased by you)"
              id="mac.licenses.history.partner.not-purchased-by-you"
            />
          ) : null}
        </StyledParagraph>
        <StyledParagraph padding={`0 ${Dimensions.units(1.25)}px`}>{description}</StyledParagraph>
        {isPurchasedByLoggedInUser && licenseId ? (
          <Button appearance="link" key={licenseId} onClick={() => setIsModalOpened(true)}>
            <FormattedMessage id="mac.licenses.view.license" defaultMessage="View license" />
          </Button>
        ) : null}
      </p>
      {licenseId ? (
        <EndUserLicenseKeyDialog
          licenseId={licenseId}
          isModalOpened={isModalOpened}
          setIsModalOpened={setIsModalOpened}
        />
      ) : null}
    </>
  );
};

const MigratedAccountHistoryTable = ({ period, handleInvoiceDownload, userPartnerId }) => {
  return (
    <StyledMigratedHistoryWrapper>
      <StyledTextLabel width="100%" textalign="start" title="History">
        <FormattedMessage {...messages.history} />
      </StyledTextLabel>
      <StyledHistoryTableWrapper>
        <table data-cy="end-user-migrated-history-table">
          <tbody>
            {period?.map((periodItem) => {
              return (
                <TableRow key={periodItem.periodId}>
                  <StyledMigratedHistoryTableData>
                    <FormattedDate
                      value={removeTimezone(periodItem.paidDate)}
                      day="2-digit"
                      month="long"
                      year="numeric"
                    />
                  </StyledMigratedHistoryTableData>
                  <StyledMigratedHistoryTableData>
                    <Button
                      appearance="link"
                      key={periodItem.orderId}
                      onClick={(e) =>
                        handleInvoiceDownload(
                          e,
                          periodItem.orderId,
                          userPartnerId === periodItem.partnerId?.toString(),
                        )
                      }
                    >
                      AT-{periodItem.orderId}
                    </Button>
                    {!userPartnerId === periodItem.partnerId?.toString() && (
                      <FormattedMessage
                        defaultMessage="(Not purchased by you)"
                        id="mac.licenses.history.partner.not-purchased-by-you"
                      />
                    )}
                  </StyledMigratedHistoryTableData>
                  <StyledMigratedHistoryTableData>
                    <StyledParagraph padding={`0 ${Dimensions.units(1.25)}px`}>
                      {periodItem.description}
                    </StyledParagraph>
                  </StyledMigratedHistoryTableData>
                </TableRow>
              );
            })}
          </tbody>
        </table>
      </StyledHistoryTableWrapper>
    </StyledMigratedHistoryWrapper>
  );
};

const AccountHistoryAsPartner = ({
  accountHistoryAsPartner,
  userPartnerId,
  isMigrated = false,
}) => {
  const dispatch = useDispatch();
  const handleInvoiceDownload = (e, cartId, isPurchasedByLoggedInUser) => {
    e.stopPropagation();

    if (isPurchasedByLoggedInUser) {
      traceUFOPress(ORDER_INVOICE_ACTION_PDF_UFO_NAME);
      dispatch(fetchInvoicePDF(cartId));
    } else {
      dispatch(redirect(route()));
    }
  };

  accountHistoryAsPartner?.sort((a, b) => {
    return new Date(a.paidDate).getTime() - new Date(b.paidDate).getTime();
  });

  if (isMigrated) {
    return (
      <MigratedAccountHistoryTable
        period={accountHistoryAsPartner}
        handleInvoiceDownload={handleInvoiceDownload}
        userPartnerId={userPartnerId}
      />
    );
  }
  return (
    <StyledHistoryWrapper data-cy="history-table">
      {accountHistoryAsPartner?.map((period) => {
        return (
          <AccountHistoryItem
            key={period.periodId}
            period={period}
            handleInvoiceDownload={handleInvoiceDownload}
            userPartnerId={userPartnerId}
          />
        );
      })}
    </StyledHistoryWrapper>
  );
};

export default AccountHistoryAsPartner;
